import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  attestControl,
  listComplianceControls,
  listControlAttestations,
  listUsers,
  updateComplianceControl,
  type AttestationAction,
  type ControlStatus,
  type ControlView,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

const STATUS_STYLE: Record<ControlStatus, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "border border-border text-text-secondary" },
  defined: { label: "Defined", cls: "bg-accent-muted text-accent" },
  approved: { label: "Approved", cls: "bg-success/15 text-success" },
  stale: { label: "Stale", cls: "bg-warning/15 text-warning" },
};

const NEXT_ACTION: Record<string, { action: AttestationAction; label: string } | null> = {
  none: { action: "prepared", label: "Prepare for sign-off" },
  prepared: { action: "reviewed", label: "Review" },
  reviewed: { action: "approved", label: "Approve" },
  approved: null,
};

export function ComplianceControlsPage() {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState<string>("all");
  const { data: controls, isLoading } = useQuery({
    queryKey: ["compliance-controls"],
    queryFn: listComplianceControls,
  });

  const openId = params.get("open");
  const open = useMemo(
    () => controls?.find((c) => c.control.id === openId) ?? null,
    [controls, openId],
  );

  const categories = useMemo(
    () => [...new Set((controls ?? []).map((c) => c.control.category))].sort(),
    [controls],
  );
  const shown = (controls ?? []).filter(
    (c) => category === "all" || c.control.category === category,
  );

  return (
    <div>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="page-title">Control library</h1>
          <p className="mt-2 text-text-secondary">
            Our controls, mapped across ISO 27001 and SOC 2. Each definition is signed off
            prepare → review → approve — by different people, enforced.
          </p>
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </header>

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Ref</th>
                <th className="px-4 py-2.5 font-semibold">Control</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
                <th className="px-4 py-2.5 font-semibold">Frequency</th>
                <th className="px-4 py-2.5 font-semibold">Maps to</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => (
                <tr
                  key={v.control.id}
                  className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                  onClick={() => setParams({ open: v.control.id })}
                >
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">
                    {v.control.ref}
                  </td>
                  <td className="px-4 py-2.5 text-text-heading">{v.control.name}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {v.control.owner?.name ?? <span className="text-warning">unassigned</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {v.control.frequency.replace("_", " ")}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-text-muted">
                    {v.mappings.map((m) => m.ref).join(", ")}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <StatusPill status={v.status} blockerCount={v.blockers.length} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <ControlModal view={open} onClose={() => setParams({})} />}
    </div>
  );
}

function StatusPill({ status, blockerCount }: { status: ControlStatus; blockerCount: number }) {
  const s = STATUS_STYLE[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
      {blockerCount > 0 && <span className="opacity-70">· {blockerCount}</span>}
    </span>
  );
}

function ControlModal({ view, onClose }: { view: ControlView; onClose: () => void }) {
  const qc = useQueryClient();
  const c = view.control;
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const { data: att } = useQuery({
    queryKey: ["compliance-attestations", c.id],
    queryFn: () => listControlAttestations(c.id),
  });
  const [statement, setStatement] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [c.id]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-controls"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
    void qc.invalidateQueries({ queryKey: ["compliance-attestations", c.id] });
  };

  const attest = useMutation({
    mutationFn: (action: AttestationAction) => attestControl(c.id, action, statement),
    onSuccess: () => {
      setStatement("");
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const setOwner = useMutation({
    mutationFn: (ownerId: string | null) => updateComplianceControl(c.id, { ownerId }),
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });

  const next = NEXT_ACTION[view.cycleStage];

  return (
    <Modal open onClose={onClose} title={`${c.ref} — ${c.name}`} wide>
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill status={view.status} blockerCount={view.blockers.length} />
          <span className="text-text-muted">
            {c.category} · {c.frequency.replace("_", " ")} ·{" "}
            {c.sourceStatus === "provisional" ? "provisional (unverified against standard)" : "verified"}
          </span>
          <label className="ml-auto flex items-center gap-2 text-text-secondary">
            Owner
            <select
              value={c.ownerId ?? ""}
              onChange={(e) => setOwner.mutate(e.target.value || null)}
              className="rounded-md border border-border bg-bg-card px-2 py-1 text-sm text-text-heading"
            >
              <option value="">— unassigned —</option>
              {users
                ?.filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <Section title="What we do">
          <p className="whitespace-pre-wrap text-text-secondary">{c.description}</p>
        </Section>

        <Section title="How we prove it operated">
          <p className="whitespace-pre-wrap text-text-secondary">{c.testProcedure || "—"}</p>
        </Section>

        <Section title="Satisfies">
          <div className="flex flex-wrap gap-1.5">
            {view.mappings.map((m) => (
              <span
                key={m.requirementId}
                title={m.title}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  m.frameworkKey === "iso27001"
                    ? "bg-accent-muted text-text-heading"
                    : "bg-bg-secondary text-text-secondary"
                }`}
              >
                {m.frameworkKey === "iso27001" ? "ISO" : "SOC 2"} {m.ref}
              </span>
            ))}
          </div>
        </Section>

        {view.blockers.length > 0 && (
          <Section title="Blockers">
            <ul className="space-y-1">
              {view.blockers.map((b) => (
                <li key={b.rule} className="text-warning">
                  <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                  {b.message}
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section
          title={`Sign-off trail${
            att ? (att.chain.valid ? " · chain verified" : " · CHAIN BROKEN") : ""
          }`}
        >
          {att && att.attestations.length === 0 && (
            <p className="text-text-muted">No attestations yet.</p>
          )}
          <ul className="space-y-1.5">
            {att?.attestations.map((a) => (
              <li key={a.id} className="text-text-secondary">
                <span className="font-medium text-text-heading">{a.actor?.name ?? "?"}</span>{" "}
                {a.action} · {new Date(a.occurredAt).toLocaleString()}
                {a.contentHash !== att.attestations[att.attestations.length - 1]?.contentHash &&
                view.cycleStage !== "approved" ? (
                  <span className="ml-2 text-xs text-text-muted">(earlier version)</span>
                ) : null}
                {a.statement && <div className="ml-4 text-xs italic text-text-muted">“{a.statement}”</div>}
              </li>
            ))}
          </ul>

          {next && (
            <div className="mt-3 flex gap-2">
              <input
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                placeholder={`What are you asserting? (required)`}
                className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
              />
              <button
                onClick={() => attest.mutate(next.action)}
                disabled={!statement.trim() || attest.isPending}
                className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {next.label}
              </button>
            </div>
          )}
          {error && <p className="mt-2 text-xs text-danger">{error}</p>}
        </Section>
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}
